import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useSound } from "../../utils/sound/SoundProvider";
import useGameBase from '../../hooks/useGameBase';
import SpectatorPerspectiveSelector from "./SpectatorPerspectiveSelector";

const RANK_ORDER = ["A","2","3","4","5","6","7","8","9","10","J","Q","K"];

function GoFishGame({ gameState, gamePlayers = [], user = {}, onGameAction, socket, roomId, isSpectator = false }) {
  // --- Spectator Perspective logic (modular, opt-in, Canasta/Bingo style) ---
  const perspectiveOptions = useMemo(() => {
    if (Array.isArray(gamePlayers) && gamePlayers.length > 0) return gamePlayers;
    if (Array.isArray(gameState?.players) && gameState.players.length > 0) return gameState.players;
    return [];
  }, [gamePlayers, gameState?.players]);

  const [spectatorViewPlayerId, setSpectatorViewPlayerId] = useState(null);
  const perspectivePlayer = useMemo(() => {
    if (!isSpectator || !spectatorViewPlayerId) return null;
    return perspectiveOptions.find((player) => {
      const playerId = player?.userId || player?.socketId || player?.id || player?.username;
      return String(playerId) === String(spectatorViewPlayerId);
    }) || null;
  }, [perspectiveOptions, isSpectator, spectatorViewPlayerId]);

  useEffect(() => {
    if (!isSpectator) {
      setSpectatorViewPlayerId(null);
      return;
    }
    if (!Array.isArray(perspectiveOptions) || perspectiveOptions.length === 0) {
      setSpectatorViewPlayerId(null);
      return;
    }
    const exists = perspectiveOptions.some((player) => {
      const playerId = player?.userId || player?.socketId || player?.id || player?.username;
      return String(playerId) === String(spectatorViewPlayerId);
    });
    if (!exists) {
      const first = perspectiveOptions[0];
      setSpectatorViewPlayerId(first?.userId || first?.socketId || first?.id || first?.username || null);
    }
  }, [perspectiveOptions, isSpectator, spectatorViewPlayerId]);
  // Helper: map player IDs to names
  const playersById = useMemo(() => {
    const map = {};
    for (const p of gamePlayers) map[p.socketId] = p.username || p.name || p.socketId;
    return map;
  }, [gamePlayers]);

  // Helper: format last action using player names
  function formatLastAction(la) {
    if (!la || !la.type) return null;
    const who = playersById[la.by] || la.by;
    if (la.type === "drawFromOcean" || la.type === "goFish") {
      return `${who} drew a card`;
    }
    if (la.type === "askForRank") {
      const tgt = playersById[la.target] || la.target;
      if (la.transferCount && la.transferCount > 0)
        return `${who} asked ${tgt} for any ${la.rank}s — ${la.transferCount} card${la.transferCount === 1 ? "" : "s"} given`;
      return `${who} asked ${tgt} for any ${la.rank}s`;
    }
    if (la.type === "goFishPending") {
      const tgt = playersById[la.target] || la.target;
      if (la.target) return `${who} asked ${tgt} for any ${la.rank}s but got told to Go Fish!`;
      return `${who} must Go Fish (draw requested)`;
    }
    if (la.type === "transfer") {
      const tgt = playersById[la.target] || la.target;
      if (la.transferCount && la.transferCount > 0)
        return `${who} received ${la.transferCount} card${la.transferCount === 1 ? "" : "s"} from ${tgt}`;
      return `${who} received cards from ${tgt}`;
    }
    return null;
  }
  const { submitAction, perspectiveUser, perspectiveIds } = useGameBase({ gameState, gamePlayers, user, socket, roomId, perspectivePlayer });
  const userId = perspectiveUser?.socketId || user?.socketId;
  // Helper to find the correct hand for the current perspective (Canasta/Bingo style)
  const findPerspectiveValue = useCallback((source) => {
    if (!source || typeof source !== "object") return null;
    for (const key of perspectiveIds) {
      if (typeof key !== "undefined" && key !== null && source[key]) {
        return source[key];
      }
    }
    return null;
  }, [perspectiveIds]);
  const myHand = useMemo(() => findPerspectiveValue(gameState.playerHands) || [], [gameState.playerHands, findPerspectiveValue]);
  const drawPileCount = gameState.drawPile?.length || 0;
  const currentPlayer = gameState.currentPlayer;
  const [selectedRank, setSelectedRank] = useState(null);
  const gameAreaRef = useRef(null);
  const drawPileRef = useRef(null);
  const handWrapperRef = useRef(null);
  const groupRefs = useRef({});
  const otherDeckRefs = useRef({});
  const setsRefs = useRef({});
  const [animatedCards, setAnimatedCards] = useState([]);
  const [pendingDraws, setPendingDraws] = useState([]);
  const [pendingTransfers, setPendingTransfers] = useState([]);
  const [setAnimating, setSetAnimating] = useState(null);
  // askMode removed; selection is via clicking opponent decks
  // card-first ask flow: selectedRank indicates what we're asking for
  

  // (no queued ask/reveal logic — show minimal instructions only)
  const awaitingAskRef = useRef(null);
  const prevHandsRef = useRef(gameState.playerHands || {});
  const prevSetsRef = useRef(gameState.sets || {});
  const goFishPendingForMe = (gameState.lastAction && gameState.lastAction.type === 'goFishPending' && gameState.lastAction.by === userId);
  const sound = useSound();
  const pendingSetsRef = useRef([]);

  // Animate when a new set is created for any player
  useEffect(() => {
    const prevSets = prevSetsRef.current || {};
    const curSets = gameState.sets || {};
    for (const pid of Object.keys(curSets)) {
      const prev = prevSets[pid] || [];
      const cur = curSets[pid] || [];
      if (cur.length <= prev.length) continue;
      // New set(s) added for this player. Animate the most recent set's cards.
      const newSets = cur.slice(prev.length);
      const rank = newSets[0];

      // If there are in-flight animations (draw/transfer), or this update also added cards to the player's hand,
      // queue this set animation so transfers finish first.
      const prevHands = prevHandsRef.current || {};
      const handAdded = (gameState.playerHands && prevHands && (gameState.playerHands[pid] || []).length > (prevHands[pid] || []).length);
      const busy = animatedCards.length > 0 || (pendingDraws && pendingDraws.length > 0) || (pendingTransfers && pendingTransfers.length > 0) || setAnimating || handAdded;
      if (busy) {
        pendingSetsRef.current.push({ pid, rank });
        continue;
      }

      // Find source elements: if pid === userId, try to find groupRefs for that rank, else use otherDeckRefs
      const container = gameAreaRef.current;
      const startEl = (pid === userId) ? (groupRefs.current[rank] || handWrapperRef.current) : (otherDeckRefs.current[pid] || null);
      if (!container || !startEl) continue;

      try {
        const containerRect = container.getBoundingClientRect();
        const startRect = startEl.getBoundingClientRect();
        const startLeft = startRect.left - containerRect.left;
        const startTop = startRect.top - containerRect.top;

        // Prefer a target positioned directly under the player's hand for local-player sets.
        // Fallback to the sets element when hand wrapper isn't available.
        let tRect = null;
        const targetEl = setsRefs.current[pid] || null;
        if (pid === userId && handWrapperRef.current) {
          const handRect = handWrapperRef.current.getBoundingClientRect();
          tRect = { left: handRect.left, top: handRect.top + handRect.height + 6, width: handRect.width, height: 20 };
        } else if (targetEl) {
          tRect = targetEl.getBoundingClientRect();
        }
        if (!tRect) continue;
        const targetLeft = tRect.left - containerRect.left + (tRect.width/2) - 40;
        const targetTop = tRect.top - containerRect.top;

        // create four animated cards that collapse then fly to target
        const ids = [];
        // Determine images for the four cards. For local player, try to use actual faces from previous hand snapshot.
        const prevHandsSnap = prevHandsRef.current || {};
        const localCards = (pid === userId && prevHandsSnap[pid]) ? prevHandsSnap[pid].filter(c => c.value === rank).slice(0,4) : [];
        for (let i = 0; i < 4; i++) {
          const id = `set-${pid}-${rank}-${Date.now()}-${i}`;
          ids.push(id);
          const card = localCards[i];
          const src = card ? getCardImage(card) || '/cards/card_back.png' : '/cards/card_back.png';
          // stagger start positions slightly
          setAnimatedCards(prev => [...prev, { id, src, left: startLeft + i*6, top: startTop + i*3, width: 60, height: 84, targetLeft: targetLeft + i*2, targetTop: targetTop + i*2, transition: '' }]);
        }
        // show instruction
  setSetAnimating(pid);
  // play set-complete sound
  try { sound && sound.play && sound.play('gofish:setcomplete'); } catch (e) {}
        // collapse them to a stack briefly
        setTimeout(() => {
          setAnimatedCards(prev => prev.map(a => ids.includes(a.id) ? { ...a, left: startLeft + 12, top: startTop + 6, transition: 'left 180ms ease, top 180ms ease' } : a));
        }, 120);
        // then fly to target
        setTimeout(() => {
          setAnimatedCards(prev => prev.map(a => ids.includes(a.id) ? { ...a, left: a.targetLeft, top: a.targetTop, transition: 'left 420ms ease, top 420ms ease, transform 420ms ease' } : a));
        }, 320);
        // cleanup and clear animating
        setTimeout(() => {
          setAnimatedCards(prev => prev.filter(a => !ids.includes(a.id)));
          setSetAnimating(null);
        }, 780);
      } catch (e) {
        setSetAnimating(null);
      }
    }
    prevSetsRef.current = { ...(gameState.sets || {}) };
  }, [gameState.sets, userId, animatedCards.length, pendingDraws, pendingTransfers, setAnimating, gameState.playerHands, sound]);
  // Added 'sound' to dependency lists where effects call sound.play

  // When animatedCards and pending flags clear, process any queued set animations
  useEffect(() => {
    const isBusy = animatedCards.length > 0 || (pendingDraws && pendingDraws.length > 0) || (pendingTransfers && pendingTransfers.length > 0) || setAnimating;
    if (isBusy) return;
    const next = pendingSetsRef.current.shift();
    if (!next) return;
    // trigger animation for next queued set
    const { pid, rank } = next;
    const container = gameAreaRef.current;
    const startEl = (pid === userId) ? (groupRefs.current[rank] || handWrapperRef.current) : (otherDeckRefs.current[pid] || null);
    // Prefer hand wrapper for local player so sets land directly under the cards.
    let targetEl = setsRefs.current[pid] || null;
    if (container && startEl) {
      try {
        const containerRect = container.getBoundingClientRect();
        const startRect = startEl.getBoundingClientRect();
        const startLeft = startRect.left - containerRect.left;
        const startTop = startRect.top - containerRect.top;
        let tRect = null;
        if (pid === userId && handWrapperRef.current) {
          const handRect = handWrapperRef.current.getBoundingClientRect();
          tRect = { left: handRect.left, top: handRect.top + handRect.height + 6, width: handRect.width, height: 20 };
        } else if (targetEl) {
          tRect = targetEl.getBoundingClientRect();
        }
        if (!tRect) return;
        const targetLeft = tRect.left - containerRect.left + (tRect.width/2) - 40;
        const targetTop = tRect.top - containerRect.top;
        const ids = [];
        // For queued animations, similarly try to use real faces for local player
        const prevHandsQueued = prevHandsRef.current || {};
        const localCardsQueued = (pid === userId && prevHandsQueued[pid]) ? prevHandsQueued[pid].filter(c => c.value === rank).slice(0,4) : [];
        for (let i = 0; i < 4; i++) {
          const id = `set-queued-${pid}-${rank}-${Date.now()}-${i}`;
          ids.push(id);
          const card = localCardsQueued[i];
          const src = card ? getCardImage(card) || '/cards/card_back.png' : '/cards/card_back.png';
          setAnimatedCards(prev => [...prev, { id, src, left: startLeft + i*6, top: startTop + i*3, width: 60, height: 84, targetLeft: targetLeft + i*2, targetTop: targetTop + i*2, transition: '' }]);
        }
        setSetAnimating(pid);
  // play set-complete sound for queued animation
  try { sound && sound.play && sound.play('gofish:setcomplete'); } catch (e) {}
  setTimeout(() => {
          setAnimatedCards(prev => prev.map(a => ids.includes(a.id) ? { ...a, left: startLeft + 12, top: startTop + 6, transition: 'left 180ms ease, top 180ms ease' } : a));
        }, 120);
        setTimeout(() => {
          setAnimatedCards(prev => prev.map(a => ids.includes(a.id) ? { ...a, left: a.targetLeft, top: a.targetTop, transition: 'left 420ms ease, top 420ms ease, transform 420ms ease' } : a));
        }, 320);
        setTimeout(() => {
          setAnimatedCards(prev => prev.filter(a => !ids.includes(a.id)));
          setSetAnimating(null);
        }, 780);
      } catch (e) {
        setSetAnimating(null);
      }
    }
  }, [animatedCards.length, pendingDraws, pendingTransfers, setAnimating, userId, sound]);

  // (available ranks are implied by your hand; selection is via clicking cards)

  function getCardImage(card) {
    if (!card) return null;
    let value = String(card.value);
    let suit = card.suit;
    if (["J","Q","K","A"].includes(value)) value = value.toLowerCase();
    return `/cards/${value}_of_${suit}.png`;
  }

  // ask now happens via clicking a card after choosing Ask -> player

  function handleDraw() {
    submitAction({ type: "drawFromOcean" }).catch(()=>{});
  }

  // (ask handled by card-first flow: handleCardSelection + clicking other player's deck)

  function formatCard(c) {
    if (!c) return "";
    return `${c.value} of ${c.suit}`;
  }

  // New flow: select your own card first, then pick a player to ask.
  function handleCardSelection(rank) {
    if (currentPlayer !== userId) return;
    if (goFishPendingForMe) return; // must draw first
    // If only one other player, ask them immediately
    const others = gamePlayers.filter(p => p.socketId !== userId);
    if (others.length === 1) {
      const target = others[0].socketId;
  submitAction({ type: 'askForRank', target, rank }).catch(()=>{});
      awaitingAskRef.current = { target, rank };
      setSelectedRank(null);
      return;
    }
    // Otherwise set selected rank and prompt other players
    setSelectedRank(rank);
  }

  // selection handled by group/button click directly

  // Animate when local player draws a card (lastAction set by server)
  useEffect(() => {
    const la = gameState.lastAction;
    if (!la || !la.type) return;
    const isDraw = la.type === 'drawFromOcean' || la.type === 'goFish';
    if (!isDraw) return;
    if (la.by !== userId) return; // only animate local player's draw
    if (!la.drawn) return;

    // Helper that attempts to read DOM geometry and run the animation.
    // Sometimes refs or layout aren't ready when the state update arrives,
    // so retry several times with a short delay and prefer hand wrapper as fallback.
    let attempts = 6;
    const tryAnimate = () => {
      const container = gameAreaRef.current;
      let drawEl = drawPileRef.current;
      // As a fallback, try to find the draw pile inside the container by attribute
      if (!drawEl && container) {
        drawEl = container.querySelector('[data-draw-pile]');
      }
      // If basic DOM refs are not ready, retry
      if (!container || !drawEl) {
        attempts -= 1;
        if (attempts > 0) {
          setTimeout(tryAnimate, 100);
        }
        return;
      }

      try {
        const containerRect = container.getBoundingClientRect();
        const startRect = drawEl.getBoundingClientRect();
        const startLeft = startRect.left - containerRect.left;
        const startTop = startRect.top - containerRect.top;

        // target: prefer group ref for the drawn rank, else use hand wrapper
        const rank = la.drawn.value;
        const preferredTarget = groupRefs.current[rank] || null;
        const fallbackTarget = handWrapperRef.current || null;
        const targetEl = preferredTarget || fallbackTarget;

        // If no reasonable target present yet, retry - it's likely layout is still updating
        if (!targetEl) {
          attempts -= 1;
          if (attempts > 0) setTimeout(tryAnimate, 100);
          return;
        }

        const tRect = targetEl.getBoundingClientRect();

        // compute target so the animated card centers into the target element
        const cardWidth = 60;
        const cardHeight = 84;
        const targetWidth = tRect.width || cardWidth;
        const targetHeight = tRect.height || cardHeight;
        const targetLeft = tRect.left - containerRect.left + (targetWidth / 2) - (cardWidth / 2);
        // vertically align to middle of target area when possible
        const targetTop = tRect.top - containerRect.top + Math.max((targetHeight - cardHeight) / 2, 0);

        // For local player's draw animation show the card face; fallback to card back
        const src = getCardImage(la.drawn) || '/cards/card_back.png';
        const id = `anim-${Date.now()}`;
        const sig = `${la.drawn.value}-${la.drawn.suit}`;
  // play card draw sound
  try { sound && sound.play && sound.play('ui:card'); } catch (e) {}
  // mark this drawn card as pending so it won't render in hand until animation completes
  setPendingDraws(prev => [...prev, sig]);

        // Add animated card at start position with consistent sizing
        setAnimatedCards(prev => [...prev, { id, src, left: startLeft, top: startTop, width: cardWidth, height: cardHeight, targetLeft, targetTop, transition: '' }]);
        // trigger move on next tick
        setTimeout(() => {
          setAnimatedCards(prev => prev.map(c => c.id === id ? { ...c, left: targetLeft, top: targetTop, transition: 'left 420ms ease, top 420ms ease, transform 420ms ease' } : c));
        }, 30);
        // remove after animation and clear pending flag
        setTimeout(() => {
          setAnimatedCards(prev => prev.filter(c => c.id !== id));
          setPendingDraws(prev => prev.filter(s => s !== sig));
        }, 520);
      } catch (e) {
        // ignore geometry errors and retry briefly
        attempts -= 1;
        if (attempts > 0) setTimeout(tryAnimate, 100);
      }
    };

    // Mark the draw pile with a data attribute so the fallback query can find it
    // (the element is already referenced by ref; adding attribute is idempotent)
    if (drawPileRef.current && !drawPileRef.current.getAttribute('data-draw-pile')) {
      try { drawPileRef.current.setAttribute('data-draw-pile', '1'); } catch (e) {}
    }
    tryAnimate();
  }, [gameState.lastAction, userId, sound]);

  // Animate when any other player draws from the pile so everyone sees the card move
  useEffect(() => {
    const la = gameState.lastAction;
    if (!la || la.type !== 'drawFromOcean') return;
    if (la.by === userId) return; // handled by local effect
    if (!la.drawn) return;

    let attempts = 3;
    const tryAnimate = () => {
      const container = gameAreaRef.current;
      let drawEl = drawPileRef.current;
      if (!drawEl && container) drawEl = container.querySelector('[data-draw-pile]');
      const targetEl = otherDeckRefs.current[la.by] || null;
      if (!container || !drawEl || !targetEl) {
        attempts -= 1;
        if (attempts > 0) setTimeout(tryAnimate, 80);
        return;
      }

      try {
        const containerRect = container.getBoundingClientRect();
        const startRect = drawEl.getBoundingClientRect();
        const startLeft = startRect.left - containerRect.left;
        const startTop = startRect.top - containerRect.top;
        const tRect = targetEl.getBoundingClientRect();
        const targetLeft = tRect.left - containerRect.left + (tRect.width/2) - 28;
        const targetTop = tRect.top - containerRect.top + (tRect.height/2) - 42;

  // For other players' draw animations show the card back, not the face
  const src = '/cards/card_back.png';
  const id = `draw-anim-${Date.now()}`;
        const sig = `${la.drawn.value}-${la.drawn.suit}`;
  // play card draw sound for others
  try { sound && sound.play && sound.play('ui:card'); } catch (e) {}
  setPendingDraws(prev => [...prev, sig]);
        const cardWidth = 56; const cardHeight = 80;
        setAnimatedCards(prev => [...prev, { id, src, left: startLeft, top: startTop, width: cardWidth, height: cardHeight, targetLeft, targetTop, transition: '' }]);
        setTimeout(() => {
          setAnimatedCards(prev => prev.map(c => c.id === id ? { ...c, left: targetLeft, top: targetTop, transition: 'left 420ms ease, top 420ms ease, transform 420ms ease' } : c));
        }, 30);
        setTimeout(() => {
          setAnimatedCards(prev => prev.filter(c => c.id !== id));
          setPendingDraws(prev => prev.filter(s => s !== sig));
        }, 520);
      } catch (e) {
        // ignore
      }
    };

    // mark draw pile attribute for fallback
    if (drawPileRef.current && !drawPileRef.current.getAttribute('data-draw-pile')) {
      try { drawPileRef.current.setAttribute('data-draw-pile', '1'); } catch (e) {}
    }
    tryAnimate();
  }, [gameState.lastAction, userId, sound]);

  // detect ask-for-rank transfers (when local player asked someone)
  useEffect(() => {
    const la = gameState.lastAction;
    if (!la || !la.type) return;
    // If we initiated an ask recently, compare previous hands to detect transferred cards
    if (la.type === 'askForRank' && la.by === userId) {
      const awaiting = awaitingAskRef.current;
      if (!awaiting) return;
      const { target, rank } = awaiting;
      const prevHands = prevHandsRef.current || {};
  const prevMe = prevHands[userId] || [];
  const curMe = gameState.playerHands?.[userId] || [];

      // determine added to me (by signature)
      const sig = c => `${c.value}-${c.suit}`;
      const prevMeSigs = new Set(prevMe.map(sig));
      const added = curMe.filter(c => !prevMeSigs.has(sig(c)) && c.value === rank);
  if (added.length > 0) {
        // Animate each transferred card from target deck to our group
        const startEl = otherDeckRefs.current[target] || null;
        const targetEl = groupRefs.current[rank] || handWrapperRef.current;
        const container = gameAreaRef.current;
        if (container && startEl) {
          try {
            // Play sound indicating cards were transferred to you
            try { sound && sound.play && sound.play('gofish:allyours'); } catch (e) {}
    const runAnimation = () => {
            const containerRect = container.getBoundingClientRect();
            const startRect = startEl.getBoundingClientRect();
            const startLeft = startRect.left - containerRect.left + (startRect.width/2) - 40;
            const startTop = startRect.top - containerRect.top;
            let tLeft = startLeft + 80;
            let tTop = startTop + 10;
            if (targetEl) {
              const tRect = targetEl.getBoundingClientRect();
              tLeft = tRect.left - containerRect.left + (tRect.width/2) - 40;
              tTop = tRect.top - containerRect.top;
            }
            // add pending transfers so destination doesn't show duplicates
            const addedSigs = added.map(sig);
            setPendingTransfers(prev => [...prev, ...addedSigs]);
            // create animated cards
            const cardWidth = 60; const cardHeight = 84;
            const ids = added.map((c, i) => {
              const id = `t-${Date.now()}-${i}`;
              setAnimatedCards(prev => [...prev, { id, src: getCardImage(c) || '/cards/card_back.png', left: startLeft + i*6, top: startTop + i*3, width: cardWidth, height: cardHeight, targetLeft: tLeft + i*8, targetTop: tTop, transition: '' }]);
              return id;
            });
            // trigger move
            setTimeout(() => {
              setAnimatedCards(prev => prev.map(a => ({ ...a, left: a.targetLeft, top: a.targetTop, transition: 'left 420ms ease, top 420ms ease, transform 420ms ease' })));
            }, 40);
            // cleanup after animation
            setTimeout(() => {
              setAnimatedCards(prev => prev.filter(a => !ids.includes(a.id)));
              setPendingTransfers(prev => prev.filter(s => !addedSigs.includes(s)));
            }, 520);
    };
    runAnimation();
          } catch (e) {
            // ignore
          }
        }
      } else {
        // No transfer detected: likely Go Fish. We'll wait for a goFish lastAction.
      }
      awaitingAskRef.current = null;
    }

    // If server reports goFish for us, show message (and draw animation handled elsewhere)
    if (la.type === 'goFish' && la.by === userId) {
      // goFish message handled elsewhere if needed
    }

    // (was playing sound here; moved to top-level effect)
  }, [gameState.lastAction, gameState.playerHands, userId, sound]);


  // If server indicates a pending Go Fish (require manual draw), show instruction
  useEffect(() => {
    const la = gameState.lastAction;
    if (!la || !la.type) return;
    if (la.type === 'goFishPending' && la.by === userId) {
      try { sound && sound.play && sound.play('gofish:gofish'); } catch (e) {}
    }
  }, [gameState.lastAction, sound, userId]);

  // Show a global prompt when an ask occurs, then reveal result after a short pause
  // (basic mode: no queued ask/reveal or move summaries)

  // keep a snapshot of previous hands for comparison
  useEffect(() => {
    prevHandsRef.current = gameState.playerHands || {};
  }, [gameState.playerHands]);

  // Fallback: detect transfers by diffing hands when we asked but awaitingAskRef wasn't set
  useEffect(() => {
    const la = gameState.lastAction;
    if (!la || la.type !== 'askForRank' || la.by !== userId) return;
    // if we handled this via awaitingAskRef earlier, skip
    if (awaitingAskRef.current) return;
    const prev = prevHandsRef.current || {};
    const cur = gameState.playerHands || {};
    const sig = c => `${c.value}-${c.suit}`;
    const prevMe = prev[userId] || [];
    const curMe = cur[userId] || [];
    const prevMeSigs = new Set(prevMe.map(sig));
    const added = curMe.filter(c => !prevMeSigs.has(sig(c)));
  if (!added || added.length === 0) return;

    // try to find which player lost these cards
    const addedSigs = new Set(added.map(sig));
    for (const otherId of Object.keys(prev)) {
      if (otherId === userId) continue;
      const prevOther = prev[otherId] || [];
      const curOther = cur[otherId] || [];
      const removed = prevOther.filter(c => !curOther.some(cc => sig(cc) === sig(c)));
      const removedMatching = removed.filter(c => addedSigs.has(sig(c)));
      if (removedMatching.length > 0) {
        // animate these cards from other player's deck to our group
        const container = gameAreaRef.current;
        const startEl = otherDeckRefs.current[otherId] || null;
        const targetEl = groupRefs.current[removedMatching[0].value] || handWrapperRef.current;
        if (container && startEl) {
          try {
            // Play sound for successful transfer to the asking player
            try { sound && sound.play && sound.play('gofish:allyours'); } catch (e) {}
            const runAnim = () => {
              const containerRect = container.getBoundingClientRect();
              const startRect = startEl.getBoundingClientRect();
              const startLeft = startRect.left - containerRect.left + (startRect.width/2) - 40;
              const startTop = startRect.top - containerRect.top;
              let tLeft = startLeft + 80;
              let tTop = startTop + 10;
              if (targetEl) {
                const tRect = targetEl.getBoundingClientRect();
                tLeft = tRect.left - containerRect.left + (tRect.width/2) - 40;
                tTop = tRect.top - containerRect.top;
              }
              const cardWidth = 60; const cardHeight = 84;
              const removedSigs = removedMatching.map(sig);
              // mark pending transfers so destination doesn't render duplicates
              setPendingTransfers(prev => [...prev, ...removedSigs]);
              const ids = removedMatching.map((c, i) => {
                const id = `ft-${Date.now()}-${i}`;
                setAnimatedCards(prev => [...prev, { id, src: getCardImage(c) || '/cards/card_back.png', left: startLeft + i*6, top: startTop + i*3, width: cardWidth, height: cardHeight, targetLeft: tLeft + i*8, targetTop: tTop, transition: '' }]);
                return id;
              });
              setTimeout(() => {
                setAnimatedCards(prev => prev.map(a => ids.includes(a.id) ? { ...a, left: a.targetLeft, top: a.targetTop, transition: 'left 420ms ease, top 420ms ease, transform 420ms ease' } : a));
              }, 40);
              setTimeout(() => {
                setAnimatedCards(prev => prev.filter(a => !ids.includes(a.id)));
                setPendingTransfers(prev => prev.filter(s => !removedSigs.includes(s)));
              }, 520);
            };
            runAnim();
          } catch (e) {}
        }
        break;
      }
    }
  }, [gameState.playerHands, gameState.lastAction, userId, sound]);

  // --- Spectator Perspective Selector UI ---
  const showSpectatorPerspectiveSelector = isSpectator && perspectiveOptions.length > 0;

  return (
    <div ref={gameAreaRef} className="w-full bg-transparent dark:bg-gray-900 p-4 rounded relative overflow-visible">
      {showSpectatorPerspectiveSelector && (
        <SpectatorPerspectiveSelector
          players={perspectiveOptions}
          value={spectatorViewPlayerId || ""}
          onChange={setSpectatorViewPlayerId}
        />
      )}
      {/* Animated flying cards layer */}
      {animatedCards.map(a => (
        <img key={a.id} src={a.src} alt="card" style={{
          position: 'absolute',
          left: a.left,
          top: a.top,
          width: a.width,
          height: a.height,
          objectFit: 'contain',
          transition: a.transition,
          zIndex: 9999,
          pointerEvents: 'none'
        }} />
      ))}

      <div className="text-sm mb-2 text-gray-700 dark:text-gray-200">Draw Pile</div>
      <div className="flex items-center justify-between mb-4">
        
        <div className="flex items-center gap-3">
          {/* Draw pile visual stack */}
          <div
            role="button"
            tabIndex={0}
            ref={drawPileRef}
            onClick={() => { if (currentPlayer === userId && drawPileCount > 0) handleDraw(); }}
            onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && currentPlayer === userId && drawPileCount > 0) handleDraw(); }}
            className={`relative ${currentPlayer === userId ? 'cursor-pointer' : 'opacity-60'}`}
            style={{ width: 60, height: 84, perspective: 700 }}
            title={`Draw pile (${drawPileCount} cards)`}
          >
            {(() => {
              if (!drawPileCount || drawPileCount === 0) {
                // Empty draw pile placeholder (same size as card)
                return (
                  <div
                    style={{ position: 'absolute', left: 0, top: 0, width: '60px', height: '84px', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box', background: 'transparent' }}
                    className="border-2 border-dashed border-gray-300 dark:border-gray-600"
                  >
                    <div className="text-xs text-gray-500 dark:text-gray-300">Empty</div>
                  </div>
                );
              }
              const show = Math.min(6, drawPileCount);
              const back = '/cards/card_back.png';
              const baseLeft = 0;
              const baseTop = 0;
              const rotateRange = 6; // degrees
              const items = [];
              for (let i = 0; i < show; i++) {
                const left = baseLeft + (show - 1 - i) * 2;
                const top = baseTop + (show - 1 - i) * 1.5;
                const rot = (i - show / 2) * (rotateRange / show);
                items.push(
                  <img
                    key={i}
                    src={back}
                    alt="Card back"
                    className={((currentPlayer === userId) || goFishPendingForMe) && !selectedRank ? 'card-pulse' : ''}
                    style={{
                      position: 'absolute',
                      left: `${left}px`,
                      top: `${top}px`,
                      width: '60px',
                      height: '84px',
                      objectFit: 'cover',
                      transform: `rotate(${rot}deg) translateZ(${i * 1}px)`,
                      boxShadow: '0 6px 14px rgba(0,0,0,0.35)',
                      borderRadius: 6,
                    }}
                  />
                );
              }
              return items;
            })()}
            {/* small counter overlay */}
            <div className="absolute -bottom-1 -right-2 text-xs px-1 py-0.5 bg-black text-white dark:bg-gray-800 dark:text-gray-100 rounded">{drawPileCount}</div>
          </div>
        </div>
        <div>
          {/* Notification area: show last action with player names */}
          {gameState.lastAction && formatLastAction(gameState.lastAction) && (
            <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', top: 12, zIndex: 10000 }} className="px-3 py-1 bg-black text-white rounded-md text-sm opacity-90">
              {formatLastAction(gameState.lastAction)}
            </div>
          )}
        </div>
      </div>
      <div className="mb-4">
        <h4 className="font-semibold text-gray-800 dark:text-gray-100">Your hand ({myHand.length})</h4>
        {/* If we've selected a rank to ask for, prompt to click a player */}
        {selectedRank && (
          <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">Ask any player for <span className="font-semibold">{selectedRank}</span> by clicking their deck</div>
        )}
        <style>{`
          
          @keyframes pulseBorder { 0% { transform: rotate(2deg) scale(1.00); } 25% { transform: rotate(0deg) scale(1.00); } 50% { transform: rotate(-2deg) scale(1.00); } 75% { transform: rotate(0deg) scale(1.00); }  100% { transform: rotate(2deg) scale(1.0); } }
          .deck-pulse { border-radius: 8px; animation: pulseBorder 1.2s infinite ease-in-out; }
          @keyframes pulseCard { 0% { transform: rotate(2deg) scale(1.00); } 25% { transform: rotate(0deg) scale(1.00); } 50% { transform: rotate(-2deg) scale(1.00); } 75% { transform: rotate(0deg) scale(1.00); }  100% { transform: rotate(2deg) scale(1.0); } }
          .card-pulse { animation: pulseCard 1.2s infinite ease-in-out; }
        `}</style>
        <div ref={handWrapperRef} className="flex gap-3 mt-2 flex-wrap items-end">
          {/* Remove ranks that are already completed books for this player */}
          {(() => {
            const completed = new Set((gameState.sets?.[userId] || []));
            // Exclude any pending drawn cards (one instance per pending signature)
            let displayHand = myHand.filter(c => !completed.has(c.value));
            if (pendingDraws && pendingDraws.length > 0) {
              // remove one matching card for each pending draw signature
              const temp = [...displayHand];
              for (const sig of pendingDraws) {
                const idx = temp.findIndex(card => `${card.value}-${card.suit}` === sig);
                if (idx !== -1) temp.splice(idx, 1);
              }
              displayHand = temp;
            }
            // remove pending transfers (cards that have been added to our hand but are animating in)
            if (pendingTransfers && pendingTransfers.length > 0) {
              const temp2 = [...displayHand];
              for (const sig of pendingTransfers) {
                const idx = temp2.findIndex(card => `${card.value}-${card.suit}` === sig);
                if (idx !== -1) temp2.splice(idx, 1);
              }
              displayHand = temp2;
            }
            // Group by rank while preserving RANK_ORDER
            const groups = RANK_ORDER.filter(r => displayHand.some(c => c.value === r)).map(r => ({
              rank: r,
              cards: displayHand.filter(c => c.value === r)
            }));
            const offset = 12; // pixels to offset overlapping cards
            const cardWidth = 60; // match draw pile card size
            const cardHeight = 84; // match draw pile card size

            return groups.map((group) => {
              const n = group.cards.length;
              const containerWidth = (n - 1) * offset + cardWidth;
              const isSelectedGroup = selectedRank === group.rank;
          if (n === 1) {
                const card = group.cards[0];
                const src = getCardImage(card);
                return (
                  <button
                    key={group.rank}
        type="button"
        ref={el => { groupRefs.current[group.rank] = el; }}
        onClick={() => {
          if (currentPlayer !== userId) return;
          if (goFishPendingForMe) return;
          if (selectedRank === group.rank) setSelectedRank(null);
          else handleCardSelection(group.rank);
        }}
        className={`flex-none p-0 rounded overflow-hidden shadow ${isSelectedGroup ? 'ring-4 ring-blue-400' : 'bg-white dark:bg-gray-800'} ${(currentPlayer === userId && !selectedRank && !goFishPendingForMe) ? 'card-pulse cursor-pointer' : ''}`}
        style={{ width: cardWidth, height: cardHeight }}
                    title={`${group.rank} (1)`}
                  >
                    {src ? (
                      <img src={src} alt={formatCard(card)} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    ) : (
                      <div className="flex items-center justify-center text-xs text-gray-700 dark:text-gray-200">{formatCard(card)}</div>
                    )}
                  </button>
                );
              }
              return (
          <button
                    key={group.rank}
                    type="button"
                    ref={el => { groupRefs.current[group.rank] = el; }}
                    onClick={() => { if (currentPlayer !== userId) return; if (goFishPendingForMe) return; if (selectedRank === group.rank) setSelectedRank(null); else handleCardSelection(group.rank); }}
            className={`relative flex-none p-0  rounded overflow-visible ${isSelectedGroup ? 'ring-4 ring-blue-400' : 'bg-transparent'} ${(currentPlayer === userId && !selectedRank && !goFishPendingForMe) ? 'card-pulse cursor-pointer' : ''}`}
                    style={{ width: containerWidth, height: cardHeight }}
                    title={`${group.rank} (${n})`}
                  >
                    <div style={{ position: 'relative', width: containerWidth, height: cardHeight }}>
                      {group.cards.map((card, idx) => {
                        const left = idx * offset;
                        const z = idx;
                        const src = getCardImage(card);
                        return (
                          <img
                            key={idx}
                            src={src || undefined}
                            alt={formatCard(card)}
                            style={{
                              position: 'absolute',
                              left: `${left}px`,
                              top: 0,
                              width: `${cardWidth}px`,
                              height: `${cardHeight}px`,
                              objectFit: 'contain',
                              zIndex: z,
                              boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
                            }}
                          />
                        );
                      })}
                      {/* count badge in top-right */}
                      {n > 1 && (
                        <div className="absolute" style={{ right: 2, top: 2 }}>
                          <div className="text-xs px-1 py-0.5 bg-black text-white rounded">{n}</div>
                        </div>
                      )}
                    </div>
                  </button>
              );
            });
          })()}
        </div>
  {/* Local player's sets under their hand */}
  <div ref={el => { setsRefs.current[userId] = el; }} className="mt-2 text-xs text-gray-600 dark:text-gray-400">{(() => { const s = (gameState.sets?.[userId] || []); return s.length === 0 ? '0 Sets' : `${s.length} ${s.length === 1 ? 'Set' : 'Sets'} : ${s.join(' ')}` })()}</div>
      </div>

   

      

      {/* Other players' decks at the bottom with totals */}
      <div className="mt-6 border-t pt-4">
        <div className="mt-2 flex gap-4 items-end">
          {gamePlayers.filter(p => p.socketId !== userId).map(p => {
            const count = (gameState.playerHands?.[p.socketId] || []).length;
            const selectable = currentPlayer === userId; // only selectable on your turn
            return (
              <div key={p.socketId} className="flex flex-col items-center w-32">
                <div className="text-sm mb-2 text-center text-gray-800 dark:text-gray-100">{p.username}</div>
                <div
                  ref={el => { otherDeckRefs.current[p.socketId] = el; }}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    if (!selectable || goFishPendingForMe) return;
                    if (selectedRank) {
                      // send ask with the selected rank
                      submitAction({ type: 'askForRank', target: p.socketId, rank: selectedRank }).catch(()=>{});
                      awaitingAskRef.current = { target: p.socketId, rank: selectedRank };
                      setSelectedRank(null);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (!selectable || goFishPendingForMe) return;
                    if ((e.key === 'Enter' || e.key === ' ') && selectedRank) {
                      submitAction({ type: 'askForRank', target: p.socketId, rank: selectedRank }).catch(()=>{});
                      awaitingAskRef.current = { target: p.socketId, rank: selectedRank };
                      setSelectedRank(null);
                    }
                  }}
                  className={`relative ${(!selectable || goFishPendingForMe) ? 'opacity-50 pointer-events-none cursor-not-allowed' : (selectedRank ? 'cursor-pointer' : 'cursor-default')}`}
                  style={{ width: 56, height: 80 }}
                >
                  {(() => {
                    if (!count || count === 0) {
                      return (
                        <div style={{ position: 'absolute', left: 0, top: 0, width: '56px', height: '80px', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box', background: 'transparent' }} className="border-2 border-dashed border-gray-300 dark:border-gray-600">
                          <div className="text-xs text-gray-500 dark:text-gray-300">Empty</div>
                        </div>
                      );
                    }
                    const back = '/cards/card_back.png';
                    const show = Math.min(4, count);
                    const items = [];
                    for (let i = 0; i < show; i++) {
                      const left = i * 4;
                      const top = -i * 2;
                      items.push(
                        <img key={i} src={back} alt="back" className={(currentPlayer === userId && selectedRank) ? 'card-pulse' : ''} style={{ position: 'absolute', left: `${left}px`, top: `${top}px`, width: '56px', height: '80px', borderRadius: 6, boxShadow: '0 6px 12px rgba(0,0,0,0.25)' }} />
                      );
                    }
                    return items;
                  })()}
                  {/* count badge overlay */}
                  <div className="absolute -bottom-1 -right-2 text-xs px-1 py-0.5 bg-black text-white rounded">{count}</div>
                </div>
                {/* show this player's sets under their deck */}
                <div ref={el => { setsRefs.current[p.socketId] = el; }} className="text-xs text-gray-600 dark:text-gray-400 mt-2">{(() => { const s = (gameState.sets?.[p.socketId] || []); return s.length === 0 ? '0 Sets' : `${s.length} ${s.length === 1 ? 'Set' : 'Sets'} : ${s.join(' ')}` })()}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

GoFishGame.Guide = function GoFishGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">How to Play Go Fish</h3>

      <p className="mb-2">
        <strong>Objective:</strong> Collect the most sets (books) of four matching cards
        (for example, four Kings) by the end of the game.
      </p>

      <p className="mb-2"><strong>Players:</strong> 2–6 players</p>
      <p className="mb-2"><strong>Materials:</strong> Standard 52-card deck (no jokers)</p>

      <h4 className="font-semibold mt-3 mb-1">Setup</h4>
      <ol className="list-decimal ml-6 mb-2">
        <li>Shuffle the deck thoroughly.</li>
        <li>Deal 5 cards to each player if there are 4–6 players; deal 7 cards each for 2–3 players.</li>
        <li>Place the remaining cards face-down in the center as the draw (fishing) pile.</li>
      </ol>

      <h4 className="font-semibold mt-3 mb-1">Gameplay</h4>
      <ol className="list-decimal ml-6 mb-2">
        <li>A random player is chosen to go first; play proceeds clockwise.</li>
        <li>
          On your turn you ask another player for all cards of a specific rank that you
          already hold (for example, “Do you have any Queens?”). You must hold at least one
          card of that rank to ask.
        </li>
        <li>
          If the asked player has any cards of that rank, they must give all of them to you
          and you take another turn.
        </li>
        <li>
          If the player does not have any, they say “Go Fish!” and you draw one card from the
          draw pile. If the drawn card matches the rank you asked for, show it and take another turn;
          otherwise keep the card and the turn passes to the next player.
        </li>
        <li>
          When you collect four cards of the same rank (a “book”), place them face-up in front of you.
        </li>
      </ol>

      <h4 className="font-semibold mt-3 mb-1">Winning</h4>
      <p className="mb-2">The game ends when the draw pile is empty or all possible books are collected. The player with the most books wins.</p>

      <h4 className="font-semibold mt-3 mb-1">Tips</h4>
      <ul className="list-disc ml-6 mb-2">
        <li>Pay attention to what other players ask for — it helps you guess their hands.</li>
        <li>If the draw pile runs out, continue asking until no more books can be formed.</li>
      </ul>
    </div>
  );
};

export default GoFishGame;
